/**
 * @fileoverview Core - User Activation Tracking
 * @module features/core/activation
 * 
 * @description
 * Track user activation events including onboarding steps,
 * signup completions, and first-time actions.
 * 
 * @useCases
 * - Track onboarding step completions
 * - Monitor signup funnel
 * - Measure time to first value
 * - Identify activation bottlenecks
 * - A/B test onboarding flows
 * 
 * @metrics
 * - Activation rate
 * - Onboarding completion rate
 * - Time to activate
 * - Drop-off by step
 * - Activation by source
 * 
 * @chartData
 * - Funnel chart: Onboarding steps
 * - Line chart: Activation rate over time
 * - Bar chart: Activation by cohort/source
 * - KPI: Time to first value
 * 
 * @example
 * ```typescript
 * const activation = new Activation(webId, endpoint, apiKey);
 * 
 * // Track activation event
 * await activation.create({
 *   eventName: 'onboarding_completed',
 *   properties: { userId: 'user_123', step: 'profile_setup' }
 * });
 * ```
 */

import { FlowGridTransport } from "../../client/transport";
import { TimeSeriesData } from "../../types";

// ============================================================================
// CONFIGURATION TYPES
// ============================================================================

/**
 * Base configuration for activation events.
 */
export interface ActivationConfig {
  /** Event name */
  eventName: string;
  /** Event properties */
  properties: Record<string, unknown>;
}

/**
 * Onboarding step configuration.
 */
export interface OnboardingStepConfig {
  /** User ID */
  userId: string;
  /** Step identifier */
  stepId: string;
  /** Step name */
  stepName: string;
  /** Step order/number */
  stepOrder: number;
  /** Total steps in flow */
  totalSteps: number;
  /** Time spent on step (ms) */
  timeSpent?: number;
  /** Whether step was skipped */
  skipped?: boolean;
  /** Custom properties */
  properties?: Record<string, unknown>;
}

/**
 * First action configuration.
 */
export interface FirstActionConfig {
  /** User ID */
  userId: string;
  /** Action name */
  actionName: string;
  /** Time since signup (ms) */
  timeSinceSignup: number;
  /** Source/referrer */
  source?: string;
  /** Custom properties */
  properties?: Record<string, unknown>;
}

/**
 * Activation response from API.
 */
export interface ActivationResponse {
  eventName: string;
  properties: Record<string, unknown>;
  response: {
    tracked: boolean;
    activationId?: string;
    isFirstActivation?: boolean;
  };
  timestamp: string;
}

/**
 * Activation analytics data.
 */
export interface ActivationAnalyticsData {
  /** Summary metrics */
  summary: {
    totalSignups: number;
    activatedUsers: number;
    activationRate: number;
    avgTimeToActivate: number;
    onboardingCompletionRate: number;
  };
  /** Activation by step */
  byStep: Array<{
    stepId: string;
    stepName: string;
    started: number;
    completed: number;
    dropOff: number;
    avgTimeSpent: number;
  }>;
  /** Activation by source */
  bySource: Array<{
    source: string;
    signups: number;
    activated: number;
    activationRate: number;
  }>;
  /** Activation trends */
  trends: TimeSeriesData;
  /** Cohort activation */
  byCohort: Array<{
    cohort: string;
    users: number;
    activated: number;
    activationRate: number;
    avgTimeToActivate: number;
  }>;
}

// ============================================================================
// ACTIVATION CLASS
// ============================================================================

/**
 * Activation - Track user activation events.
 * 
 * @extends FlowGridTransport
 * 
 * @description
 * Provides activation tracking including:
 * - Generic activation events
 * - Onboarding step tracking
 * - First action tracking
 * - Signup completion
 * - Activation analytics
 * 
 * @example
 * ```typescript
 * const activation = new Activation(webId, endpoint, apiKey);
 * 
 * // Track onboarding step
 * await activation.trackOnboardingStep({
 *   userId: 'user_123',
 *   stepId: 'profile_setup',
 *   stepName: 'Set Up Profile',
 *   stepOrder: 2,
 *   totalSteps: 5
 * });
 * 
 * // Track first value action
 * await activation.trackFirstAction({
 *   userId: 'user_123',
 *   actionName: 'created_first_project',
 *   timeSinceSignup: 3600000 // 1 hour
 * });
 * ```
 */
export class Activation extends FlowGridTransport {
  /**
   * Creates and tracks an activation event.
   * 
   * @param config - Activation configuration
   * @returns Promise resolving to activation response
   */
  async create(config: ActivationConfig): Promise<ActivationResponse> {
    return await this.init(config.eventName, {
      ...config.properties,
      type: "activation",
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Tracks an onboarding step completion.
   * 
   * @param config - Onboarding step configuration
   * @returns Promise resolving to activation response
   */
  async trackOnboardingStep(config: OnboardingStepConfig): Promise<ActivationResponse> {
    return await this.init("onboarding_step", {
      type: "activation",
      subtype: "onboarding_step",
      userId: config.userId,
      stepId: config.stepId,
      stepName: config.stepName,
      stepOrder: config.stepOrder,
      totalSteps: config.totalSteps,
      progress: Math.round((config.stepOrder / config.totalSteps) * 100),
      timeSpent: config.timeSpent,
      skipped: config.skipped || false,
      ...config.properties,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Tracks onboarding completion.
   * 
   * @param userId - User ID
   * @param totalTimeSpent - Total time in onboarding (ms)
   * @param stepsCompleted - Number of steps completed
   * @param stepsSkipped - Number of steps skipped
   * @returns Promise resolving to activation response
   */
  async completeOnboarding(
    userId: string,
    totalTimeSpent: number,
    stepsCompleted: number,
    stepsSkipped: number
  ): Promise<ActivationResponse> {
    return await this.init("onboarding_completed", {
      type: "activation",
      subtype: "onboarding_complete",
      userId,
      totalTimeSpent,
      stepsCompleted,
      stepsSkipped,
      completionRate: Math.round((stepsCompleted / (stepsCompleted + stepsSkipped)) * 100),
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Tracks user's first meaningful action (activation moment).
   * 
   * @param config - First action configuration
   * @returns Promise resolving to activation response
   */
  async trackFirstAction(config: FirstActionConfig): Promise<ActivationResponse> {
    return await this.init("first_action", {
      type: "activation",
      subtype: "first_action",
      userId: config.userId,
      actionName: config.actionName,
      timeSinceSignup: config.timeSinceSignup,
      source: config.source,
      ...config.properties,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Tracks signup completion.
   * 
   * @param userId - User ID
   * @param method - Signup method (email, google, etc.)
   * @param source - Traffic source
   * @param properties - Additional properties
   * @returns Promise resolving to activation response
   */
  async trackSignup(
    userId: string,
    method: 'email' | 'google' | 'github' | 'apple' | 'sso' | string,
    source?: string,
    properties?: Record<string, unknown>
  ): Promise<ActivationResponse> {
    return await this.init("signup_completed", {
      type: "activation",
      subtype: "signup",
      userId,
      method,
      source,
      ...properties,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Tracks a user login (returning-user signal — recorded as an event, not a
   * funnel conversion).
   *
   * @param userId - User ID
   * @param method - Login method (email, google, etc.)
   * @param properties - Additional properties
   * @returns Promise resolving to activation response
   */
  async trackLogin(
    userId: string,
    method?: 'email' | 'google' | 'github' | 'apple' | 'sso' | string,
    properties?: Record<string, unknown>
  ): Promise<ActivationResponse> {
    return await this.init("login_completed", {
      type: "activation",
      subtype: "login",
      userId,
      method,
      ...properties,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Emit an `active_user` signal for an identified user so the dashboard can
   * compute DAU / WAU / MAU **by identity**. Rides the activation pipe
   * (subtype `active_user`) so the wire `event_name` is `active_user` — the
   * exact shape the active-users pipes read.
   *
   * @param userId - User ID
   * @param traits - Optional email/name for Known-visitor parity
   * @returns Promise resolving to activation response
   */
  async trackActiveUser(
    userId: string,
    traits?: { email?: string; name?: string } & Record<string, unknown>
  ): Promise<ActivationResponse> {
    const now = new Date();
    return await this.init("active_user", {
      type: "activation",
      subtype: "active_user",
      userId,
      ...(traits?.email ? { email: traits.email } : {}),
      ...(traits?.name ? { name: traits.name } : {}),
      // Day bucket lets the dashboard dedupe repeat pings within a day cheaply.
      date: now.toISOString().slice(0, 10),
      timestamp: now.toISOString()
    });
  }

}

export default Activation;
